import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FieldArray, Field, FastField } from 'formik';
import { FormGroup, Label, FormFeedback, Button, Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { get } from 'lodash';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { connect } from 'react-redux';

import DeviceLookup from 'components/DeviceLookup';
import { renderField } from 'core/utils/formik';
import CardActions from '../../../components/ui/CardActions';
import { isFetchingDeviceSchema } from '../../../store/test-form-reducer';
import DeviceStateFields from './DeviceStateFields/DeviceStateFields';

const propTypes = {
  testDevices: PropTypes.array.isRequired,
  editing: PropTypes.bool,
};

const defaultProps = {
  editing: false,
};

const blankCrmDeviceFactory = () => ({
  device_type: 'crm',
  device: null,
  location: '',
  location_other: '',
});

const blankPassiveDeviceFactory = () => ({
  device_type: 'passive',
  device: {
    manufacturer: '',
    deviceable: {
      serial_number: '',
      device_type: '',
    },
  },
  location: '',
  location_other: '',
});

const resultMask = createNumberMask({
  allowDecimal: true,
  decimalLimit: 1,
  prefix: '',
});

const renderResult = ({ field }) => {
  return (
    <div>
      <Label for="result" className="required">Result</Label>
      <MaskedInput
        className="form-control"
        mask={resultMask}
        {...field}
      />
    </div>
  );
};

class DeviceFields extends PureComponent {
  constructor(props) {
    super(props);

    this.handleLookupChange = this.handleLookupChange.bind(this);
    this.renderCrmDeviceLookup = this.renderCrmDeviceLookup.bind(this);
    this.renderPassiveDeviceLookup = this.renderPassiveDeviceLookup.bind(this);
    this.renderDeviceInputs = this.renderDeviceInputs.bind(this);
  }

  handleLookupChange(field, form, device) {
    form.setFieldValue(field.name, (device || {}).id);
  }

  renderCrmDeviceLookup(arrayHelpers, device, index) {
    const { editing } = this.props;

    return (
      <Card className="TestDevice">
        <CardBody>
          <CardTitle>CRM</CardTitle>

          {!editing &&
            <CardActions>
              <Button
                color="danger"
                size="sm"
                onClick={() => arrayHelpers.remove(index)}
              >
                Remove
              </Button>
            </CardActions>
          }

          <Row className="TestDevice__row">
            <Col>
              <FastField
                name={`test_devices.${index}.device.id`}
                component={({ field, form }) => {
                  const { name } = field;
                  const { errors, touched } = form;
                  const deviceId = get(form.values, name);

                  return (
                    <FormGroup color={(touched[name] && errors[name]) ? 'danger' : ''}>
                      <Label for="device_id" className="required">Device</Label>

                      <DeviceLookup
                        onChange={device => this.handleLookupChange(field, form, device, index)}
                        onBlur={() => form.setFieldTouched(field.name, true)}
                        deviceId={deviceId}
                        params={{ non_expired: true }}
                      />

                      {touched[name] && errors[name] && <FormFeedback>{errors[name]}</FormFeedback>}
                    </FormGroup>
                  );
                }}
              />
            </Col>

            <DeviceStateFields device={device} index={index} />

            {editing &&
              <Col className="resultCol">
                <FastField
                  name={`test_devices.${index}.result`}
                  component={renderResult}
                />
              </Col>
            }
          </Row>
        </CardBody>
      </Card>
    );
  }

  renderPassiveDeviceLookup(arrayHelpers, device, index) {
    const { editing } = this.props;

    return (
      <Card className="TestDevice">
        <CardBody>
          <CardTitle>Passive</CardTitle>
          <CardActions>
            <Button
              color="danger"
              size="sm"
              onClick={() => arrayHelpers.remove(index)}
            >
              Remove
            </Button>
          </CardActions>

          <Row className="TestDevice__PassiveFields TestDevice__row">
            <Col>
              <Field
                type="select"
                name={`test_devices.${index}.device.deviceable.device_type`}
                label="Device Type"
                component={renderField}
                required
              >
                <option />
                <option value="charcoal_canister">Charcoal Canister</option>
                <option value="electret_ion_chamber">Electret Ion Chamber</option>
              </Field>
            </Col>

            <Col>
              <Field
                type="text"
                name={`test_devices.${index}.device.manufacturer`}
                label="Manufacturer"
                component={renderField}
                required
              />
            </Col>

            <Col>
              <Field
                type="text"
                name={`test_devices.${index}.device.deviceable.serial_number`}
                label="Serial Number"
                component={renderField}
                required
              />
            </Col>

            <DeviceStateFields device={device} index={index} />

            {editing &&
              <Col className="resultCol">
                <FastField
                  name={`test_devices.${index}.result`}
                  component={renderResult}
                />
              </Col>
            }
          </Row>
        </CardBody>
      </Card>
    );
  }

  renderDeviceInputs(arrayHelpers, device, index) {
    const { device_type } = device;

    switch (device_type) {
      case 'crm':
        return this.renderCrmDeviceLookup(arrayHelpers, device, index);
      case 'passive':
        return this.renderPassiveDeviceLookup(arrayHelpers, device, index);
      default:
        throw new Error(`unknown device type ${device_type}`);
    }
  }

  render() {
    const { testDevices, editing, isFetching } = this.props;

    return (
      <FormGroup>
        <Label for="test_devices">Devices</Label>

        <FieldArray
          name="test_devices"
          render={(arrayHelpers) => {
            return (
              <div className="DeviceFields">
                <ul>
                  {testDevices.map((device, index) => (
                    <li key={index}>{this.renderDeviceInputs(arrayHelpers, device, index)}</li>
                  ))}
                </ul>

                {!editing &&
                  <div className="DeviceFields__actions">
                    <Button
                      type="button"
                      onClick={() => arrayHelpers.push(blankCrmDeviceFactory())}
                      disabled={isFetching}
                    >
                      Add CRM Device
                    </Button>

                    <Button
                      type="button"
                      onClick={() => arrayHelpers.push(blankPassiveDeviceFactory())}
                      disabled={isFetching}
                    >
                      Add Passive Device
                    </Button>
                  </div>
                }
              </div>
            );
          }}
        />
      </FormGroup>
    );
  }
}

DeviceFields.propTypes = propTypes;
DeviceFields.defaultProps = defaultProps;

const mapStateToProps = state => ({
  isFetching: isFetchingDeviceSchema(state),
});

export default connect(mapStateToProps)(DeviceFields);



// WEBPACK FOOTER //
// ./src/features/test/TestForm/DeviceFields.js